<!--
View-datatable using vuetify datatable

Has header set, as well as search bar and translations.
Additionally, has different buttons that can be set to receive events for a specific row.
Buttons can also be shown depending on a true/false value given in the row.
-->
<template>
  <div>
    <v-card class="general-datatable ma-1" elevation="0">
      <v-card-title class="pa-5 pb-4">
        <p v-if="title" class="mr-4">{{ title }}</p>

        <v-btn v-if="enabledSelect && clearSelectionBtn" :disabled="rowSelected" outlined color="primary"
               @click="() => { selected=[];}">
          <v-icon left>playlist_remove</v-icon>
          {{ $t('buttons:select-none') }}
        </v-btn>
        <!-- Action button for selected rows -->
        <v-btn v-if="enabledSelect" :disabled="rowSelected" outlined color="primary" @click="clickedActionButton">
          <v-icon left>{{ actionIcon }}</v-icon>
          {{ selectedText }}
        </v-btn>
        <v-btn v-if="addBtn" outlined color="primary" route :to="addBtnRoute">
          <v-icon>add</v-icon>
          <span class="d-none d-sm-block">{{ addBtnText }}</span>
        </v-btn>
        <v-spacer></v-spacer>
        <v-text-field
            v-model="search"
            v-if="enabledSearch"
            append-icon="search"
            :value="computedSearch"
            :label="$t('components:common.search')"
            single-line
            hide-details
            clearable
        />
      </v-card-title>
      <v-card-text class="pa-0">
        <v-data-table
            v-model="selected"
            :headers="headers"
            :items="data"
            :search="search"
            :sort-by.sync="computedSort"
            :sort-desc.sync="computedDesc"
            :loading="loading"
            :loading-text="$t('components:common.loading')"
            :no-data-text="$t('common:no-data-found')"
            :no-results-text="$t('components:datatable.noResults')"
            :footer-props="{itemsPerPageText: $t('components:datatable.itemsPerPage') ,
                                    itemsPerPageAllText: $t('common:all') }"
            :show-select="enabledSelect"
            @current-items="currentItems"
        >
          <template v-if="enabledSelect && disableSelectAll" v-slot:[`header.data-table-select`]>

          </template>
          <!-- Column as link -->
          <template v-if="linkRoute" v-slot:[`item.link`]="{ item }">
            <router-link :to="linkRoute + item[linkId]" class="font-weight-bold text-decoration-none">
              {{ item[linkName] }}
            </router-link>
          </template>

          <template v-if="linkRoute2" v-slot:[`item.link2`]="{ item }">
            <router-link :to="linkRoute2 + item[linkId2]" class="font-weight-bold text-decoration-none">
              {{ item[linkName2] }}
            </router-link>
          </template>

          <template v-if="linkRoute3" v-slot:[`item.link3`]="{ item }">
            <router-link :to="linkRoute3 + item[linkId3]" class="font-weight-bold text-decoration-none">
              {{ item[linkName3] }}
            </router-link>
          </template>

          <!-- Column as link with multiple IDs -->
          <template v-if="linkNameMultipleIDs" v-slot:[`item.linkMultipleIDs`]="{ item }">
            <router-link
                :to="linkRouteMultipleIDs1 + item[linkIdMultipleIDs1] + linkRouteMultipleIDs2 + item[linkIdMultipleIDs2]"
                class="font-weight-bold text-decoration-none">
              {{ item[linkNameMultipleIDs] }}
            </router-link>
          </template>

          <!-- Have to specifically set footer page text info for translation -->
          <template #[`footer.page-text`]="props">
            {{ props.pageStart }}-{{ props.pageStop }} {{ $t("components:datatable.page") }} {{ props.itemsLength }}
          </template>

          <!-- Additional image column -->
          <template v-slot:[`item.image`]="{ item }">
            <!-- check if the item has an image path and set it, otherwise set the alternative image value -->
            <v-img v-if="item != null && Object.prototype.hasOwnProperty.call(item, image) && item[image]"
                   max-height="64px"
                   max-width="64px"
                   :src="item[image]"/>
            <v-img max-height="64px"
                   max-width="64px"
                   v-else :src="require('@/assets/' + imageAlt)"/>
          </template>

          <!-- Additional checkboxes that can be set to specific properties -->
          <template v-slot:[`item.checkbox`]="{ item }">
            <v-icon>
              {{ checkShowCheckbox(checkboxOnProperty, item) ? "check_box" : "check_box_outline_blank" }}
            </v-icon>
          </template>

          <template v-slot:[`item.checkbox2`]="{ item }">
            <v-icon>
              {{ checkShowCheckbox(checkboxOnProperty2, item) ? "check_box" : "check_box_outline_blank" }}
            </v-icon>
          </template>

          <template v-slot:[`item.checkbox3`]="{ item }">
            <v-icon>
              {{ checkShowCheckbox(checkboxOnProperty3, item) ? "check_box" : "check_box_outline_blank" }}
            </v-icon>
          </template>

          <template v-slot:[`item.checkbox4`]="{ item }">
            <v-icon>
              {{ checkShowCheckbox(checkboxOnProperty4, item) ? "check_box" : "check_box_outline_blank" }}
            </v-icon>
          </template>

          <!-- Additional buttons that can emit functions -->
          <template v-slot:[`item.controls`]="{ item }">
            <!-- Event for the first button -->
            <v-btn v-if="checkShowButton(icon, dependsOnProperty, item)" class="ma-2" icon :color="iconColor ? iconColor : 'primary'"
                   @click="clickedButton1(item)">
              <v-icon>{{ icon }}</v-icon>
            </v-btn>

            <!-- Event for the second button -->
            <v-btn v-if="checkShowButton(icon2, dependsOnProperty2, item)" class="ma-2" icon :color="iconColor2 ? iconColor2 : 'primary'"
                   @click="clickedButton2(item)">
              <v-icon>{{ icon2 }}</v-icon>
            </v-btn>

            <!-- Event for the third button -->
            <v-btn v-if="checkShowButton(icon3, dependsOnProperty3, item)" class="ma-2" icon :color="iconColor3 ? iconColor3 : 'primary'"
                   @click="clickedButton3(item)">
              <v-icon>{{ icon3 }}</v-icon>
            </v-btn>
          </template>

          <!-- Additional state icon btns for success and failure-->
          <template v-slot:[`item.statesBtns`]="{ item }">
            <!-- Event for the success button -->
            <v-btn v-if="checkShowButton(trueBtnIcon, dependsOnProperty, item)" class="mb-2 ma-sm-2" outlined small
                  color="success" @click="clickedTrueButton(item)">
              <v-icon>{{ trueBtnIcon }}</v-icon>
            </v-btn>

            <!-- Event for the failure button -->
            <v-btn v-if="checkShowButton(falseBtnIcon, dependsOnProperty2, item)" class="ma-0 ma-sm-2" outlined small
                  color="error" @click="clickedFalseButton(item)">
              <v-icon>{{ falseBtnIcon }}</v-icon>
            </v-btn>
          </template>

          <!-- Badges -->
          <template v-slot:[`item.badge`]="{ item }">
            <v-chip :color="item.badgeColor" small>
              <span class="font-weight-bold">
                {{ item.status }}
              </span>
            </v-chip>
          </template>

          <!-- boolean fields -->
          <template v-for="field in trueFalseDecorateFields" v-slot:[`item.${field}`]="{ item }" >
            <v-icon :key="field">
              {{returnLabel(item, field)}}
            </v-icon>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>

export default {
  name: "DataTable",
  props: {
    sortBy: String, // according to which column should be sorted by default
    sortDesc: Boolean, // bool to sort either asc oder desc
    title: String, //title of the datatable
    enabledSearch: Boolean, // bool for enabling/disabling search field
    cachedSearch: String, // chached Search String from prev Search
    enabledSelect: Boolean, // bool to enable/disable selecting a row
    actionIcon: String, // icon for the action button
    disableSelectAll: Boolean, // bool for enabling/disabling select all
    addBtn: Boolean, // optional Button for adding object
    addBtnRoute: String, // route for optional add Button
    addBtnText: String, // text of optional add Button
    headers: Array, //header information
    data: Array, //table data
    icon: String, //icon for the first button, if not given, button is not shown
    icon2: String, //^ second button
    icon3: String, //^ third button
    iconColor: String,
    iconColor2: String,
    iconColor3: String,
    trueBtnIcon: String, // icon for true btn
    falseBtnIcon: String, // ^ false btn
    dependsOnProperty: String, //will only show first button if row property with this value is set to true
    dependsOnProperty2: String, //^ second button
    dependsOnProperty3: String, //^ third button
    checkboxOnProperty: String, //if set, will allow to show a checkbox depending on the given property
    checkboxOnProperty2: String, //^ second checkbox
    checkboxOnProperty3: String, //^ third checkbox,
    checkboxOnProperty4: String, //^ third checkbox,
    image: String, //name of property containing image path to display
    imageAlt: String, //alternative image if image couldn't be loaded
    selectedText: String, // text for button for selected rows
    linkName: String, // text for link which will be displayed
    linkName2: String, // ^ second link
    linkName3: String, // ^ third link
    linkRoute: String, // Route for link to direct to
    linkRoute2: String, // ^ second link
    linkRoute3: String, // ^ third link
    linkId: String, // ID for link for routing
    linkId2: String, // ^ second link
    linkId3: String, // ^ third link
    linkRouteMultipleIDs1: String, // first route part to direct to
    linkRouteMultipleIDs2: String, // second route part to direct to
    linkIdMultipleIDs1: String, // first ID for link for routing
    linkIdMultipleIDs2: String, // second ID for link for routing
    linkNameMultipleIDs: String, // text for link which will be displayed
    addTopScrollBar: Boolean, // add a horizontal scrollbar on top of table above headers
    loading: Boolean, // indicates loading of table (blue bar color-rotating), loading Text
    clearSelectionBtn: Boolean, // shows "Clear selection" Button
    trueFalseDecorateFields: Array, // boolean columns which should show symbols instead of true/false
  },
  data() {
    return {
      selected: [],
      search: '',
      sortByLocal: this.sortBy,
      sortDescLocal: this.sortDesc,
      renderTopScrollBar: true,
      tableWrapperWidth: null,
      tableWidth: null,
    }
  },
  computed: {
    rowSelected() {
      if (this.selected.length > 0) {
        return false;
      } else {
        return true;
      }
    },
    computedSort: {
      get() {
        if(this.sortByLocal) {
          return this.sortByLocal;
        }
        return null;
      },
      set(value) {
        this.sortByLocal = value;
      }
    },
    computedDesc: {
      get() {
        if(this.sortDescLocal) {
          return this.sortDescLocal;
        }
        return null;
      },
      set(value) {
        this.sortDescLocal = value;
      }
    },
    computedSearch: {
      get() {
        if (this.search) {
          this.$emit('cacheCommit', this.search);
          return this.search
        }
        this.$emit('cacheCommit', null);
        return null
      },
      set(value) {
        this.search = value;
      }
    }
  },
  mounted() {
    if(this.cachedSearch) {
      this.search = this.cachedSearch
    }
    if (this.addTopScrollBar) {
      // add event listener when screen is being resized, so we can resize top scroll bar
      window.addEventListener('resize', this.resizeTopBar);
    }
  },
  updated() {
    // have to do this in updated because in mounted, the false widths are retrieved from the table elements
    if (this.renderTopScrollBar && this.addTopScrollBar && this.data.length > 0) {
      let tableWrapper = document.getElementsByClassName('v-data-table__wrapper')[0];
      this.tableWrapperWidth = tableWrapper.offsetWidth;
      // copied addTopScrollBar-code (updated(), mounted(), variables ) from DataTablePaginated,
      // but here there is no need for 52px-offset like in DataTablePaginated
      this.tableWidth = document.getElementsByTagName('table')[0].getBoundingClientRect().width ;

      // create new div for the scrollbar and set width and class
      let scrollbar = document.createElement('div');
      scrollbar.style.width = this.tableWidth + "px";
      scrollbar.className = 'scrollbar';

      // create new div for the scrollbar-wrapper and set width and class
      let scrollbarWrapper = document.createElement('div');
      scrollbarWrapper.className = 'scrollbar-wrapper';
      scrollbarWrapper.style.width = this.tableWrapperWidth + "px";
      // add the scrollbar inside the scrollbar-wrapper
      scrollbarWrapper.appendChild(scrollbar);

      // add the scrollbarWrapper with the scrollbar before the table element
      tableWrapper.before(scrollbarWrapper);

      // when top scroll bar is being scrolled, also scroll the bottom one
      scrollbarWrapper.addEventListener('scroll', () => {
        tableWrapper.scrollLeft = scrollbarWrapper.scrollLeft;
      })

      // when bottom scroll bar is being scrolled, also scroll the top one
      tableWrapper.addEventListener('scroll', () => {
        scrollbarWrapper.scrollLeft = tableWrapper.scrollLeft;
      })

      this.renderTopScrollBar = false;
    } else if (!this.renderTopScrollBar) {
      this.resizeTopBar();
    }
  },
  watch: {
    selected() {
      this.$emit('selection-changed',this.selected);
    },
    search() {
      this.computedSearch = this.search
    },
    data() {
      this.selected =  [];
      //when data gets read in, set the property fields to the template fields
      //this way we can trick vue into searching/filtering our custom template slots without using custom sorts
      /* eslint-disable */
      /** @TODO
       *   283:9  error  Unexpected mutation of "data" prop  vue/no-mutating-props
       */
      for (var i = 0; i < this.data.length; i++) {
        this.data[i].link = this.data[i][this.linkName];
        this.data[i].link2 = this.data[i][this.linkName2];
        this.data[i].link3 = this.data[i][this.linkName3];
        this.data[i].linkMultipleIDs = this.data[i][this.linkNameMultipleIDs];
        this.data[i].checkbox = this.data[i][this.checkboxOnProperty];
        this.data[i].checkbox2 = this.data[i][this.checkboxOnProperty2];
        this.data[i].checkbox3 = this.data[i][this.checkboxOnProperty3];
        this.data[i].checkbox4 = this.data[i][this.checkboxOnProperty4];
      }
      /* eslint-enable */
    }
  },
  methods: {
    clickedActionButton() {
      this.$emit('clickedActionButton', this.selected);
    },
    clickedButton1(item) {
      this.$emit('clickedButton1', item);
    },
    clickedButton2(item) {
      this.$emit('clickedButton2', item);
    },
    clickedButton3(item) {
      this.$emit('clickedButton3', item);
    },
    clickedTrueButton(item) {
      this.$emit('clickedTrueButton', item);
    },
    clickedFalseButton(item) {
      this.$emit('clickedFalseButton', item);
    },
    currentItems(items){
      this.$emit('currentItems',items);
    },
    checkShowButton(icon, property, item) {
      if (icon == null) {
        return false;
      }
      if (property == null) {
        return true;
      }
      //check if the item in the given row has the required property and return it
      if (Object.prototype.hasOwnProperty.call(item, property)) {
        return item[property];
      }
      return false;
    },
    checkShowCheckbox(property, item) {
      if (property == null) {
        return false;
      }
      //check if the item in the given row has the required property and return it
      if (Object.prototype.hasOwnProperty.call(item, property)) {
        return item[property];
      }
      return false;
    },
    resizeTopBar() {
      // only run if tableWrapperWidth and tableWidth have been initialized before
      if (this.tableWrapperWidth && this.tableWidth) {
        this.tableWrapperWidth = document.getElementsByClassName('v-data-table__wrapper')[0].offsetWidth;
        this.tableWidth = document.getElementsByTagName('table')[0].getBoundingClientRect().width;

        // update the widths of the scrollbar and scrollbar-wrapper
        document.getElementsByClassName('scrollbar-wrapper')[0].style.width = this.tableWrapperWidth + "px";
        document.getElementsByClassName('scrollbar')[0].style.width = this.tableWidth + "px";
      }
    },
    returnLabel(item, field) {
      if (item[field] === true) {
        return 'done';
      }
      else if (item[field] === false) {
        return 'block';
      }
      else if (item[field] === null || item[field] === undefined) {
        return '';
      }
    },
  }
}

</script>
<style src="@/modules/core/components/DataTable/DataTable.scss" lang="scss"/>
